/**
 * Mock data for development without a running FastAPI backend.
 * Mirrors the real API shape from src/api/client.ts.
 *
 * To switch from mock to real API:
 *  1. Remove the mock import from query hooks (src/hooks/useLeads.ts, etc.)
 *  2. Use the real api functions directly.
 *
 * All IDs are UUIDs v4 (static for determinism in dev).
 */

import type {
  Contact,
  CurrentUser,
  Deal,
  DealEvent,
  DealStep,
  LeadListItem,
  OpsTask,
  Pipeline,
} from '@/types'

// ---- Auth ----

export const mockUser: CurrentUser = {
  id: 'user-rahu-001',
  email: 'rahu@touchbrand.ru',
  // NOTE: backend returns 'operator' for all cabinet_users (seed.sql).
  // 'rahu' here is a mock convenience label — real API sends 'operator'.
  // UI display name comes from display_name, NOT role. Never use role for access control.
  role: 'operator',
  display_name: 'Раху',
  is_active: true,
}

// ---- Pipeline (all 19 stages, TZ 3.3) ----

export const mockPipeline: Pipeline = {
  id: 'pipeline-client-lifecycle',
  name: 'Client Lifecycle',
  stages: [
    { key: 'new_lead', name: 'Новый лид', phase: 'sales', sort: 10, status: 'active', gate_key: null, allowed_next: ['conversation', 'lost'], allowed_back: [] },
    { key: 'conversation', name: 'Разговор', phase: 'sales', sort: 20, status: 'active', gate_key: null, allowed_next: ['won', 'proposal_sent', 'lost'], allowed_back: ['new_lead'] },
    { key: 'proposal_sent', name: 'КП отправлено', phase: 'sales', sort: 60, status: 'active', gate_key: null, allowed_next: ['won', 'lost'], allowed_back: ['conversation'] },
    { key: 'won', name: 'Договор + предоплата 50%', phase: 'sales', sort: 70, status: 'active', gate_key: null, allowed_next: ['onboarding'], allowed_back: [] },
    { key: 'onboarding', name: 'Онбординг · доступы', phase: 'delivery', sort: 80, status: 'manual', gate_key: null, allowed_next: ['build'], allowed_back: [] },
    { key: 'build', name: 'Сборка · БЗ+промпт', phase: 'delivery', sort: 90, status: 'manual', gate_key: null, allowed_next: ['integration'], allowed_back: ['onboarding'] },
    { key: 'integration', name: 'Подключение каналов', phase: 'delivery', sort: 100, status: 'manual', gate_key: 'gate_2', allowed_next: ['testing'], allowed_back: ['build'] },
    { key: 'testing', name: 'Тестирование', phase: 'delivery', sort: 110, status: 'manual', gate_key: null, allowed_next: ['launch'], allowed_back: ['build'] },
    { key: 'launch', name: 'Запуск', phase: 'delivery', sort: 120, status: 'manual', gate_key: 'gate_test', allowed_next: ['hypercare'], allowed_back: [] },
    { key: 'hypercare', name: 'Сопровождение 2 нед', phase: 'account', sort: 130, status: 'manual', gate_key: 'gate_3', allowed_next: ['active', 'at_risk'], allowed_back: [] },
    { key: 'active', name: 'Активен', phase: 'account', sort: 140, status: 'manual', gate_key: null, allowed_next: ['upsell', 'renewal', 'at_risk'], allowed_back: [] },
    { key: 'upsell', name: 'Апсейл', phase: 'account', sort: 150, status: 'manual', gate_key: null, allowed_next: ['active', 'renewal', 'at_risk'], allowed_back: [] },
    { key: 'renewal', name: 'Продление', phase: 'account', sort: 160, status: 'manual', gate_key: null, allowed_next: ['active', 'at_risk', 'churned'], allowed_back: [] },
    { key: 'at_risk', name: 'Риск оттока', phase: 'account', sort: 170, status: 'manual', gate_key: null, allowed_next: ['active', 'churned'], allowed_back: [] },
    { key: 'lost', name: 'Потеряно', phase: 'terminal', sort: 900, status: 'active', gate_key: null, allowed_next: [], allowed_back: [] },
    { key: 'churned', name: 'Отвал', phase: 'terminal', sort: 910, status: 'active', gate_key: null, allowed_next: [], allowed_back: [] },
  ],
}

// ---- Contacts ----

export const mockContacts: Contact[] = [
  {
    id: 'contact-001',
    tenant_id: 'touchbrand_demo',
    display_name: 'Алексей Петров',
    phone: '+7 903 123 4567',
    phone_norm: '79031234567',
    email: 'petrov@example.com',
    business_niche: 'Ветеринарные клиники',
    stage: 'active',
    tags: ['горячий'],
    notes: 'Позвонить до пятницы',
    merged_into: null,
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'contact-002',
    tenant_id: 'touchbrand_demo',
    display_name: 'Марина Соколова',
    phone: '+7 916 234 5678',
    phone_norm: '79162345678',
    email: null,
    business_niche: 'Стоматология',
    stage: 'new',
    tags: [],
    notes: null,
    merged_into: null,
    created_at: new Date(Date.now() - 20 * 60 * 1000).toISOString(), // 20 min ago
    updated_at: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
  },
  {
    id: 'contact-003',
    tenant_id: 'touchbrand_demo',
    display_name: 'ООО «Стройтех»',
    phone: '+7 495 345 6789',
    phone_norm: '74953456789',
    email: 'info@stroytech.ru',
    business_niche: 'Строительство',
    stage: 'qualified',
    tags: ['повторный'],
    notes: null,
    merged_into: null,
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
  },
]

// ---- Deals ----

export const mockDeals: Deal[] = [
  // new_lead — unclaimed (shows in leads inbox)
  {
    id: 'deal-001',
    tenant_id: 'touchbrand_demo',
    contact_id: 'contact-002',
    pipeline_id: 'pipeline-client-lifecycle',
    stage_key: 'new_lead',
    title: 'Марина Соколова / Стоматология',
    role_proposed: null,
    value: null,
    currency: 'RUB',
    status: 'open',
    lost_reason: null,
    is_repeat: false,
    claimed_by: null,
    claimed_at: null,
    payment_plan: 'split_50_50',
    paid_amount: 0,
    paid_at: null,
    second_tranche_due: null,
    config: {},
    opened_at: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
    closed_at: null,
    created_at: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
    contact: mockContacts[1],
    gates: [],
    steps: [],
  },
  // new_lead — claimed by Раху
  {
    id: 'deal-002',
    tenant_id: 'touchbrand_demo',
    contact_id: 'contact-003',
    pipeline_id: 'pipeline-client-lifecycle',
    stage_key: 'new_lead',
    title: 'ООО «Стройтех» / Строительство',
    role_proposed: null,
    value: null,
    currency: 'RUB',
    status: 'open',
    lost_reason: null,
    is_repeat: true,
    claimed_by: 'user-rahu-001',
    claimed_at: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    payment_plan: 'split_50_50',
    paid_amount: 0,
    paid_at: null,
    second_tranche_due: null,
    config: {},
    opened_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    closed_at: null,
    created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    contact: mockContacts[2],
    gates: [],
    steps: [],
  },
  // call_held — gate_1 not yet passed
  {
    id: 'deal-003',
    tenant_id: 'touchbrand_demo',
    contact_id: 'contact-001',
    pipeline_id: 'pipeline-client-lifecycle',
    stage_key: 'conversation',
    title: 'Алексей Петров / Ветеринария',
    role_proposed: 'Консультант',
    value: 80000,
    currency: 'RUB',
    status: 'open',
    lost_reason: null,
    is_repeat: false,
    claimed_by: 'user-igor-001',
    claimed_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    payment_plan: 'split_50_50',
    paid_amount: 0,
    paid_at: null,
    second_tranche_due: null,
    config: {},
    opened_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    closed_at: null,
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    // Lead context fields
    source_channel: 'telegram',
    first_message: 'Хочу автоматизировать запись клиентов в нашей ветклинике',
    contact: mockContacts[0],
    gates: [{ gate_key: 'gate_1', result: 'pending', decided_at: null, payload: null }],
    steps: [],
  },
]

// ---- Leads inbox items ----

export const mockLeads: LeadListItem[] = [
  {
    deal_id: 'deal-001',
    contact_id: 'contact-002',
    display_name: 'Марина Соколова',
    contact_channel: 'telegram',
    contact_handle: '@marina_s',
    created_at: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
    business_niche: 'Стоматология',
    first_message: 'Хочу подключить ИИ-ассистента для записи пациентов',
    claimed_by: null,
    claimed_by_name: null,
    is_repeat: false,
    source_channel: 'telegram',
  },
  {
    deal_id: 'deal-002',
    contact_id: 'contact-003',
    display_name: 'ООО «Стройтех»',
    contact_channel: 'web_form',
    contact_handle: null,
    created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    business_niche: 'Строительство',
    first_message: 'Нас интересует автоответчик для заявок',
    claimed_by: 'user-rahu-001',
    claimed_by_name: 'Раху',
    is_repeat: true,
    source_channel: 'web_form',
  },
]

// ---- Deal Steps (PLAYBOOK for call_held stage) ----

export const mockDealSteps: DealStep[] = [
  {
    deal_id: 'deal-003',
    step_key: 'call_held:1',
    label: 'Провести аудит бизнеса',
    done: true,
    done_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    done_by: 'user-igor-001',
    note: null,
    is_gate: false,
    gate_key: null,
  },
  {
    deal_id: 'deal-003',
    step_key: 'call_held:2',
    label: 'Рассчитать стоимость (Ворота 1)',
    done: false,
    done_at: null,
    done_by: null,
    note: null,
    is_gate: true,
    gate_key: 'gate_1',
  },
]

// ---- Deal Events (timeline) ----

export const mockDealEvents: DealEvent[] = [
  {
    type: 'stage',
    payload: { from: 'new_lead', to: 'conversation' },
    created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    by: 'Игорь',
  },
  {
    type: 'price_calculated',
    payload: { total: 80000, first_tranche: 40000, second_tranche: 40000 },
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    by: 'Раху',
  },
  {
    type: 'claimed',
    payload: { by: 'user-igor-001' },
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    by: 'Игорь',
  },
  {
    type: 'stage',
    payload: { from: 'new_lead', to: 'conversation' },
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 5 * 60 * 1000).toISOString(),
    by: 'Тоня',
  },
]

// ---- Ops Tasks ----

export const mockOpsTask: OpsTask = {
  id: 'task-calc-001',
  tenant_id: 'touchbrand_demo',
  deal_id: 'deal-003',
  contact_id: 'contact-001',
  kind: 'calc_price',
  status: 'done',
  params: { channels: ['max'], upsells: [] },
  result: {
    contract_total: 80000,
    first_tranche: 40000,
    second_tranche: 40000,
    subscription_base: 4900,
    breakdown: [{ label: 'Базовый пакет + MAX', amount: 80000 }],
  },
  error: null,
  attempts: 1,
  created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  started_at: new Date(Date.now() - 2 * 60 * 60 * 1000 + 1000).toISOString(),
  finished_at: new Date(Date.now() - 2 * 60 * 60 * 1000 + 3000).toISOString(),
}
